// WbHaulierSearch hook — server-side debounced typeahead against
// GET /wb/hauliers?name=<q>&per_page=20.
//
// Replaces the previous per_page=100 "load-once-and-filter-client-side"
// pattern used on the vehicle / driver detail pages, which silently
// truncated for tenants with more than 100 hauliers. The server caps
// per_page at 100 (see WEIGHBRIDGE_SERVER_CONTRACT.md "Pagination
// envelope") so a per_page=20 query stays well under the ceiling and
// keeps response payloads small for high-traffic typeahead use.
//
// The query is enabled whenever the trimmed term is non-empty. The
// consumer (`WbHaulierSearch`) drives debouncing — this hook just
// performs the GET for whatever string it's handed.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { Pagination, WbHaulier } from './useWbHauliers';

interface HaulierSearchResponse {
  hauliers: WbHaulier[];
  pagination: Pagination;
}

export function useWbHaulierSearch(q: string) {
  const trimmed = q.trim();
  const enabled = trimmed.length > 0;
  return useQuery<HaulierSearchResponse>({
    queryKey: queryKeys.wb.hauliers({ name: trimmed, per_page: 20 }),
    queryFn: async () => {
      const res = await apiClient.get<HaulierSearchResponse>(
        `${endpoints.wb.hauliers}?name=${encodeURIComponent(trimmed)}&per_page=20`,
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}
